// Global
import React from 'react';
import { tv } from 'tailwind-variants';

// Local
import { Content, PageSpecific } from '.generated/Feature.CW.model';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import ActionCardSection from 'helpers/ActionCardSection';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import classNames from 'classnames';
import useIsEditing from 'lib/hooks/useIsEditing';
export type HeroHeaderProps = Content.HeroHeader;
export type pageSpecific = PageSpecific.BasePage & {
  fields: {
    PageType?: {
      fields?: {
        Value?: Field<string>;
      };
    };
  };
};

export const CardRight = (props: HeroHeaderProps): JSX.Element => {
  const { heroActionCardsList } = props?.fields || {};
  const { base, HeadingWrapper, Title, Description, HeroImage, CardWrapper, ActionCardWrapper } =
    TAILWIND_VARIANTS();
  const isEditing = useIsEditing();
  const hasHeroActionCards = Array.isArray(heroActionCardsList) && heroActionCardsList.length > 0;

  return (
    <section data-component="authorable/shared/content/HeroHeader">
      <div className={base()}>
        <ImageWrapper className={HeroImage()} field={props?.fields?.heroImage} />
        <div className={classNames(HeadingWrapper(), 'lg:justify-end')}>
          <div className={CardWrapper()}>
            {(props?.fields?.cardHeading?.value !== '' || isEditing) && (
              <h1 className={Title()}>
                <PlainTextWrapper field={props?.fields?.cardHeading} />
              </h1>
            )}
            {(props?.fields?.cardDescription?.value !== '' || isEditing) && (
              <p className={Description()}>
                <PlainTextWrapper field={props?.fields?.cardDescription} />
              </p>
            )}
            <div className="mt-12">
              <LinkWrapper
                field={props?.fields?.cardPrimaryButton}
                ctaVariant="primary"
                className="lg:inline-block block"
              />
            </div>
          </div>
          {/* <div>
            {alternateLanguageText && (
              <LinkWrapper
                field={customLanguageLink}
                ctaVariant="hyperlink"
                ctaIcon="arrow-right"
              />
            )}
          </div> */}
        </div>
      </div>
      {hasHeroActionCards && (
        <div className={ActionCardWrapper()}>
          <ActionCardSection actionCardsList={heroActionCardsList ? heroActionCardsList : []} />
        </div>
      )}
    </section>
  );
};

export const CardLeft = (props: HeroHeaderProps): JSX.Element => {
  const { heroActionCardsList } = props?.fields || {};
  const { base, HeadingWrapper, Title, Description, HeroImage, CardWrapper, ActionCardWrapper } =
    TAILWIND_VARIANTS();
  const isEditing = useIsEditing();
  const hasHeroActionCards = Array.isArray(heroActionCardsList) && heroActionCardsList.length > 0;

  return (
    <section data-component="authorable/shared/content/HeroHeader">
      <div className={base()}>
        <ImageWrapper className={HeroImage()} field={props?.fields?.heroImage} />
        <div className={HeadingWrapper()}>
          <div className={CardWrapper()}>
            {(props?.fields?.cardHeading?.value !== '' || isEditing) && (
              <h1 className={Title()}>
                <PlainTextWrapper field={props?.fields?.cardHeading} />
              </h1>
            )}
            {(props?.fields?.cardDescription?.value !== '' || isEditing) && (
              <p className={Description()}>
                <PlainTextWrapper field={props?.fields?.cardDescription} />
              </p>
            )}

            <div className="mt-12">
              <LinkWrapper
                field={props?.fields?.cardPrimaryButton}
                ctaVariant="primary"
                className="lg:inline-block block"
              />
            </div>
          </div>
          {/* <div>
            {alternateLanguageText && (
              <LinkWrapper
                field={customLanguageLink}
                ctaVariant="hyperlink"
                ctaIcon="arrow-right"
              />
            )}
          </div> */}
        </div>
      </div>
      {hasHeroActionCards && (
        <div className={ActionCardWrapper()}>
          <ActionCardSection actionCardsList={heroActionCardsList ? heroActionCardsList : []} />
        </div>
      )}
    </section>
  );
};
export const HeroHeader = (props: HeroHeaderProps): JSX.Element => {
  return <CardLeft {...props} />;
};

export default HeroHeader;

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    style: 'primary',
  },
  slots: {
    base: ['relative', 'theme-blue', 'bg-theme-bg', 'lg:py-[91px]'],
    CardWrapper: [
      'theme-white',
      'lg:p-16',
      'p-10',
      'lg:rounded-md',
      'bg-white',
      'lg:shadow-default',
      'lg:max-w-[522px]',
      'relative',
      'z-[1]',
    ],
    HeroImage: [
      'lg:absolute',
      'lg:top-0',
      'lg:left-0',
      'w-screen', // 100vw
      'h-full', // Matches parent height
      'object-cover', // Optional: ensures the image covers entire space
      'object-top',
      'lg:z-0', // Behind content
    ],
    HeadingWrapper: [
      'lg:max-w-screen-xxl',
      'm-auto',
      'flex',
      'lg:flex-row',
      'flex-col',
      'lg:px-20',
      'px-0',
    ],
    Title: [
      'font-centuryGothic',
      'lg:text-[48px]',
      'text-[40px]',
      'font-bold',
      'lg:leading-[58px]',
      'leading-[48px]',
      'text-gray-900',
      'mb-12',
    ],
    Description: ['font-nunitoSans', 'text-8', 'font-normal', 'leading-[26px]', 'text-gray-900'],
    ActionCardWrapper: [
      'lg:max-w-screen-xxl',
      'm-auto',
      'relative',
      'lg:-top-[35px]',
      'theme-white',
      'lg:bg-transparent',
      'bg-theme-bg',
      'lg:px-20',
      'px-10',
      'lg:py-0',
      'py-[40px]',
    ],
    cardSection: ['bg-white', 'rounded-md', 'shadow-default', 'border-gray-50'],
  },
  variants: {},
});
